import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { draftsApi } from '@/utils/api';
import { toast } from 'sonner';
import { FileAudio, FileText, Trash2, ArrowRight, Clock, Mic } from 'lucide-react';
import { relTime } from '@/utils/time';
import { CardsSideNav } from '@/components/CardsSideNav';

function fmtDur(s) {
  if (s == null) return '—';
  const m = Math.floor(s / 60);
  const sec = Math.round(s % 60);
  return `${m}:${String(sec).padStart(2, '0')}`;
}

export default function DraftsPage() {
  const navigate = useNavigate();
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await draftsApi.list();
      setDrafts(res.data.drafts || []);
    } catch (err) {
      toast.error('Failed to load drafts');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this draft and its audio? This cannot be undone.')) return;
    try {
      await draftsApi.remove(id);
      setDrafts(prev => prev.filter(d => d.id !== id));
      toast.success('Draft deleted');
    } catch (err) {
      toast.error('Failed to delete draft');
    }
  };

  return (
    <div className="pagewrap" data-testid="drafts-page">
      <div className="page-head">
        <div>
          <div className="pt">Drafts</div>
          <div className="ps">Paused recordings and unfinished captures. Resume any time, then turn them into an Action Card.</div>
        </div>
      </div>
      <div className="ac-layout">
        <aside className="folder-rail"><CardsSideNav /></aside>
        <div className="ac-list-col">
      {loading ? (
        <div className="ce-clips">
          {[0, 1, 2].map(i => <div key={i} className="ce-clip" style={{ height: 64, opacity: .5 }} />)}
        </div>
      ) : drafts.length === 0 ? (
        <div className="empty" data-testid="drafts-empty">
          <div className="ei"><FileText /></div>
          <p style={{ fontWeight: 700, color: 'var(--ink)', marginBottom: 4 }}>No drafts yet</p>
          <p style={{ color: 'var(--mute)', fontSize: 13.5, marginBottom: 16 }}>
            Pause a recording or hit “Save draft” on a capture and it’ll wait for you here.
          </p>
          <button className="ce-gen" style={{ margin: '0 auto' }} onClick={() => navigate('/capture')} data-testid="drafts-new-capture-button">
            Start a capture <ArrowRight size={15} />
          </button>
        </div>
      ) : (
        <div className="drafts-list" data-testid="drafts-list">
          {drafts.map((d, i) => {
            const clips = d.clips || [];
            const totalDur = clips.reduce((s, c) => s + (c.duration || 0), 0);
            return (
              <div
                key={d.id}
                className="draft-row"
                onClick={() => navigate(`/capture?draft=${d.id}`)}
                data-testid={`draft-row-${i}`}
              >
                <span className="dr-ic">{clips.length > 0 ? <Mic /> : <FileText />}</span>
                <div className="dr-body">
                  <div className="dr-title">{d.title || 'Untitled draft'}</div>
                  <div className="dr-meta">
                    <span><Clock size={12} />{relTime(d.updated_at)}</span>
                    {clips.length > 0 && <span><FileAudio size={12} />{clips.length} clip{clips.length > 1 ? 's' : ''}{totalDur ? ` · ${fmtDur(totalDur)}` : ''}</span>}
                    {d.notes?.trim() && <span><FileText size={12} />Notes</span>}
                    {(d.tags || []).slice(0, 2).map(t => <span key={t} className="dr-tag">{t}</span>)}
                  </div>
                </div>
                {clips.length > 0 && (
                  <button className="dr-resume" data-testid={`draft-resume-recording-${i}`}
                    title="Resume recording — add a new segment"
                    onClick={(e) => { e.stopPropagation(); navigate(`/capture?draft=${d.id}&mode=record`); }}>
                    <Mic size={14} />Resume
                  </button>
                )}
                <button className="dr-del" onClick={(e) => remove(d.id, e)} data-testid={`draft-delete-${i}`} aria-label="Delete draft">
                  <Trash2 size={16} />
                </button>
                <ArrowRight className="dr-go" size={16} />
              </div>
            );
          })}
        </div>
      )}
        </div>
      </div>
    </div>
  );
}
